import { useState, useEffect } from 'react';
import { serviceFeedbackApi } from '../lib/api';
import { parseHash } from '../lib/sites';

// A customer's feedback on a finished service visit:
// orders.silverservice.com/#service-feedback?t=…, from the email the office
// sends from the job (Service desk → a completed job → Customer feedback).
// No sign-in — the link names one job and is the only credential. The answer
// goes to the job and is emailed to Olaf and Andrea.
const VISIT_WORDS = {
  repair: 'repair', maintenance: 'maintenance visit', filter_change: 'filter change', refill: 'HLF refill',
  inspection: 'calibration check', site_inspection: 'site inspection', estimate: 'estimate visit',
  install: 'installation', removal: 'equipment pick-up', other: 'service visit',
};

function fmtDay(d) {
  return d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : '';
}

export default function ServiceFeedback() {
  const token = parseHash().params.get('t') || '';
  const [visit, setVisit] = useState(null);
  const [error, setError] = useState('');
  const [answer, setAnswer] = useState({ satisfied: null, questions: '', name: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    serviceFeedbackApi.get(token).then(setVisit).catch((e) => setError(e.message));
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (answer.satisfied === null) { setError('Please choose yes or no.'); return; }
    setBusy(true); setError('');
    try { setVisit(await serviceFeedbackApi.send(token, answer)); setSent(true); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  const visitText = visit && [
    `Your ${VISIT_WORDS[visit.type] || VISIT_WORDS.other}`,
    visit.customerName ? `at ${visit.customerName}` : '',
    visit.doneOn ? `on ${fmtDay(visit.doneOn)}` : '',
    visit.technician ? `with ${visit.technician}` : '',
  ].filter(Boolean).join(' ');

  return (
    <div style={st.page}>
      <div style={st.brandBar}><img src="/logo-full.png" alt="Silver Service" style={st.logo} /></div>
      <div style={st.card}>
        {!visit && !error && <p style={st.muted}>Loading…</p>}
        {!visit && error && <><h1 style={st.h1}>This link has expired</h1><p>{error}</p></>}
        {visit && sent && (
          <>
            <h1 style={st.h1}>Thank you!</h1>
            <p>{answer.satisfied
              ? "We're glad it's working well. Your answer has gone to our service team."
              : "We're sorry it isn't right. Your answer has gone to our service team and someone will be in touch soon."}</p>
          </>
        )}
        {visit && !sent && (
          <form onSubmit={submit} style={st.form}>
            <h1 style={st.h1}>How did our visit go?</h1>
            <p style={st.muted}>{visitText}{visit.label ? ` (${visit.label})` : ''}.</p>
            {visit.answered && <p style={st.note}>You already answered on {new Date(visit.answered.at).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}. Sending again replaces that answer.</p>}
            <fieldset style={st.fieldset}>
              <legend style={st.label}>Are you satisfied with the repair?</legend>
              <div style={st.choices}>
                {[[true, 'Yes'], [false, 'No']].map(([v, text]) => (
                  <button key={text} type="button" onClick={() => setAnswer((a) => ({ ...a, satisfied: v }))}
                    style={answer.satisfied === v ? { ...st.choice, ...st.choiceOn } : st.choice} aria-pressed={answer.satisfied === v}>{text}</button>
                ))}
              </div>
            </fieldset>
            <label style={st.field}>
              <span style={st.label}>Any questions about the repair?</span>
              <textarea style={st.textarea} rows={5} value={answer.questions} onChange={(e) => setAnswer((a) => ({ ...a, questions: e.target.value }))}
                placeholder={answer.satisfied === false ? "Tell us what still isn't right" : 'Optional'} maxLength={2000} />
            </label>
            <label style={st.field}>
              <span style={st.label}>Your name</span>
              <input style={st.input} value={answer.name} onChange={(e) => setAnswer((a) => ({ ...a, name: e.target.value }))} placeholder="Optional" maxLength={200} />
            </label>
            {error && <p style={st.err}>{error}</p>}
            <button type="submit" style={st.submit} disabled={busy}>{busy ? 'Sending…' : 'Send'}</button>
          </form>
        )}
      </div>
    </div>
  );
}

const RED = '#A50B18';
const st = {
  page: { minHeight: '100vh', background: '#f5f5f5', padding: '0 16px 3rem', fontFamily: "'Inter', sans-serif", color: '#222' },
  brandBar: { background: '#fff', padding: '1.25rem 1rem', textAlign: 'center', margin: '0 -16px 1.5rem', borderBottom: `3px solid ${RED}` },
  logo: { height: 90, width: 'auto', display: 'inline-block' },
  card: { maxWidth: 560, margin: '0 auto', background: '#fff', border: '1px solid #e4e0d9', borderRadius: 12, padding: '1.75rem 1.5rem 2rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', boxSizing: 'border-box' },
  form: { display: 'flex', flexDirection: 'column', gap: '1.1rem' },
  h1: { fontSize: '1.5rem', margin: '0 0 .25rem', color: '#222' },
  muted: { color: '#666', margin: 0, lineHeight: 1.5 },
  note: { background: '#fff8e6', border: '1px solid #f3dca5', borderRadius: 8, padding: '8px 12px', margin: 0, fontSize: '.9rem' },
  fieldset: { border: 'none', padding: 0, margin: 0 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontWeight: 600, fontSize: '.95rem', marginBottom: 6, padding: 0 },
  choices: { display: 'flex', gap: 10 },
  choice: { flex: 1, padding: '14px 0', fontSize: '1.05rem', background: '#fff', border: '1px solid #d5d0c8', borderRadius: 8, cursor: 'pointer', color: '#333' },
  choiceOn: { background: RED, borderColor: RED, color: '#fff', fontWeight: 700 },
  textarea: { border: '1px solid #d5d0c8', borderRadius: 6, padding: '0.65rem 0.8rem', fontSize: '1rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', resize: 'vertical' },
  input: { border: '1px solid #d5d0c8', borderRadius: 6, padding: '0.65rem 0.8rem', fontSize: '1rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  err: { color: '#c0392b', margin: 0 },
  submit: { padding: '14px 18px', background: RED, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '1rem', cursor: 'pointer' },
};
